import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import { useCurrentLeague } from "@/contexts";
import { useScopedHref } from "@/hooks";
import { buildScopedPath, getTournamentManagerHref } from "@/utils/common";
import { LICENSE_TYPE_LIST_HREFS } from "@/utils/license";

import { useVisibleLeagueNavGroups } from "../DashboardSidebar/LeagueNav/useVisibleLeagueNavGroups";
import { NavLinkItemType } from "../NavLink";
import { useNavLinkGroups } from "../NavLink/useNavLinkGroups";

import {
  GlobalSearchItem,
  GlobalSearchItemType,
  LeagueContextSearchItem,
} from "./GlobalSearch.types";
import { GlobalSearchMode } from "./useGlobalSearchMode";

/**
 * Maps each entity search-item type to the sidebar nav-link that gates access
 * to its destination list page. The main action navigates to that list page,
 * so it must respect the same permissions the sidebar enforces — e.g. a
 * non-super-admin searching a platform must not be sent to the platforms page,
 * and nobody should land on a page they can't access. `page` items are excluded
 * because they are already permission-filtered before reaching search.
 */
const SEARCH_ITEM_NAV_LINK: Partial<Record<GlobalSearchItemType, NavLinkItemType>> = {
  user: "users",
  tournament: "tournaments",
  club: "clubs",
  team: "teams",
  league: "leagues",
  license: "licenses",
  transaction: "transactions",
  organization: "organizations",
  platform: "platforms",
};

const LEAGUE_SEARCH_ITEM_NAV_LINK: Partial<Record<GlobalSearchItemType, string>> = {
  user: "athletes",
  tournament: "tournaments",
  team: "teams",
  transaction: "transactions",
  rankingRule: "rankings",
  rankingCategory: "rankingCategories",
  tournamentCategory: "tournamentCategories",
  rankingPointBlock: "rankingPoints",
  news: "news",
  partner: "partners",
  album: "gallery",
};

/**
 * SuperAdmin fallback hits live outside the caller's `(platformId, federationId)`
 * scope, so a normal scoped deep-link would land on a list page that filters
 * the row out. Override the platform / organization filters to `all` so the
 * row is visible, and route to the *list* page (not the edit page) for the
 * entity types whose list supports deep-link `?search=<id>`.
 */
export const SUPER_ADMIN_FILTERS = "platformIds=all&organizationIds=all";

/**
 * Where a search row goes when it is selected.
 *
 * - `isExternal: false` — a root-relative Admin path (already run through
 *   `useScopedHref`), navigated in place.
 * - `isExternal: true` — a destination outside Admin: today only the Manager
 *   app, reached through the `/manager-redirect` hop. Must open in a new tab so
 *   the Admin surface the user searched from stays put.
 *
 * Always hand the result to {@link useOpenSearchItemTarget} rather than calling
 * `navigate` on `href` directly.
 */
export type SearchItemTarget = {
  href: string;
  isExternal: boolean;
};

export const useGetSearchItemTarget = (mode: GlobalSearchMode = "usual") => {
  const scopedHref = useScopedHref();
  const navLinkGroups = useNavLinkGroups();
  const currentLeague = useCurrentLeague();
  const visibleLeagueNavGroups = useVisibleLeagueNavGroups(currentLeague);

  /**
   * Scoped path for a league-context hit, anchored to the hit's OWN scope.
   *
   * `useScopedHref` injects the CURRENT platform + organization and only the
   * league comes from the row, so a super-admin hit from another platform came
   * out as `/platform/<CURRENT>/entity/<CURRENT>/league/<FOREIGN>/…` — a
   * mismatched triple. The foreign league then fails to resolve, its capability
   * set falls closed, and the destination page renders Not Found (the detail
   * request is never even issued). Building the whole triple from the row fixes
   * that, and switching platform/entity via a direct URL is explicitly honoured
   * by the scope contract (see docs/scoped-routes.md).
   *
   * Falls back to `useScopedHref` when the row carries no scope pair, which is
   * the pre-contract behaviour and still correct for an in-scope hit.
   */
  const leagueScopedHref = (item: LeagueContextSearchItem, restPath: string): string => {
    // BOTH halves of the pair are required. With no platform there is nothing to
    // switch to; with no federation there is no `/entity/:o` segment, and every
    // league-context destination is registered as a `league/:leagueId/…` child of
    // one — `buildScopedPath` drops the league when the org is null, yielding a
    // path that matches no route. Borrowing the CURRENT org instead would pair a
    // foreign platform with a local federation: the same mismatched triple this
    // whole function exists to stop, and one the route enforcer honours as-is.
    // Either way, fall back to the pre-contract behaviour, which is still right
    // for an in-scope hit.
    if (item.platformId === null || item.federationId === null) {
      return scopedHref(`/league/${item.leagueId}/${restPath}`);
    }

    return buildScopedPath(item.platformId, item.federationId, item.leagueId, restPath);
  };

  // The visible nav-link groups already apply the sidebar's permission logic
  // (super-admin-only groups, org-data gating, feature flags), so membership
  // here mirrors exactly what the user is allowed to navigate to.
  const accessibleNavLinkTypes = useMemo(
    () => new Set(navLinkGroups.flatMap((group) => group.items.map((item) => item.type))),
    [navLinkGroups]
  );
  const accessibleLeagueNavTypes = useMemo(
    () => new Set(visibleLeagueNavGroups.flatMap((group) => group.items.map((item) => item.type))),
    [visibleLeagueNavGroups]
  );

  /**
   * Root-relative, already-scoped Admin path for a row. Reached by every type
   * except a tournament that has an `internalId` — those go to Manager instead.
   */
  const getInternalPath = (option: GlobalSearchItem): string | undefined => {
    switch (option.type) {
      case "page":
        return scopedHref(option.to);
      case "user":
        if (mode === "usual" && currentLeague) {
          return scopedHref(`/league/${currentLeague.id}/athletes?search=${option.user.id}`);
        }
        return mode === "superAdmin"
          ? scopedHref(`/users?search=${option.user.id}&${SUPER_ADMIN_FILTERS}`)
          : scopedHref(`/users?search=${option.user.id}`);
      case "club":
        return mode === "superAdmin"
          ? scopedHref(`/operators?search=${option.club.id}&${SUPER_ADMIN_FILTERS}`)
          : scopedHref(`/operators?search=${option.club.id}`);
      case "team":
        if (mode === "usual" && currentLeague) {
          return scopedHref(`/league/${currentLeague.id}/teams?search=${option.team.id}`);
        }
        return mode === "superAdmin"
          ? scopedHref(`/teams?search=${option.team.id}&${SUPER_ADMIN_FILTERS}`)
          : scopedHref(`/teams?search=${option.team.id}`);
      case "league":
        return mode === "superAdmin"
          ? scopedHref(`/tours?search=${option.league.id}&${SUPER_ADMIN_FILTERS}`)
          : scopedHref(`/tours/edit/${option.league.id}`);
      case "license": {
        const editPath = `${LICENSE_TYPE_LIST_HREFS[option.license.type ?? "license"]}/edit/${option.license.id}`;
        // A license belongs to a `(platform, federation)` pair. When the hit
        // carries one, open the editor directly in that scope — the super-admin
        // list-page detour existed only because the row was invisible in the
        // caller's own scope, and following it through to the editor still
        // landed on the wrong scope. Falls back to the old behaviour when the
        // backend cannot resolve the pair.
        if (option.platformId !== null) {
          return buildScopedPath(option.platformId, option.license.federationId, null, editPath);
        }
        return mode === "superAdmin"
          ? scopedHref(`/licenses?search=${option.license.id}&${SUPER_ADMIN_FILTERS}`)
          : scopedHref(editPath);
      }
      case "transaction":
        if (mode === "usual" && currentLeague) {
          return scopedHref(
            `/league/${currentLeague.id}/transactions?search=${option.transaction.id}`
          );
        }
        return mode === "superAdmin"
          ? scopedHref(`/transactions?search=${option.transaction.id}&${SUPER_ADMIN_FILTERS}`)
          : scopedHref(`/transactions?search=${option.transaction.id}`);
      case "tournament":
        return mode === "usual" && currentLeague
          ? scopedHref(`/league/${currentLeague.id}/tournament/${option.tournament.id}/matches`)
          : scopedHref(`/tournament/${option.tournament.id}/matches`);
      case "platform":
        return scopedHref(`/platforms?search=${option.platform.id}`);
      case "organization":
        return mode === "superAdmin"
          ? scopedHref(`/entities?search=${option.organization.id}&${SUPER_ADMIN_FILTERS}`)
          : scopedHref(`/entities?search=${option.organization.id}`);
      // Every league-context row routes through `leagueScopedHref`, which
      // anchors the destination to the ROW's platform / federation / league
      // rather than the caller's current scope — see its docblock.
      case "rankingRule":
        return leagueScopedHref(
          option,
          `rankings/rules?ruleSearch=${encodeURIComponent(option.id)}`
        );
      case "rankingCategory":
        return leagueScopedHref(option, `ranking-category/edit/${option.id}`);
      case "tournamentCategory":
        return leagueScopedHref(option, `tournament-category/edit/${option.id}`);
      case "rankingPointBlock":
        // The block detail endpoint dual-accepts a public `rpb_…` id or a legacy
        // numeric id, and the positions page passes the raw route id straight
        // through — so route via the public `id` now that `internalId` is gone.
        return leagueScopedHref(option, `ranking-point/edit/${option.id}`);
      case "news":
        // The news edit route validates a `nws_…` public id (UpdateNewsPage
        // gates on isNewsPublicId), so it must use the public `id`, not the
        // legacy numeric `internalId` — the latter always fails validation.
        return leagueScopedHref(option, `news/edit/${option.id}`);
      case "partner":
        // The partner detail endpoint dual-accepts a public `prt_…` id or a
        // legacy numeric id, so route via the public `id`.
        return leagueScopedHref(option, `partners/edit/${option.id}`);
      case "album":
        return leagueScopedHref(option, `gallery/edit/${option.id}`);
      default:
        return undefined;
    }
  };

  const getSearchItemTarget = (option: GlobalSearchItem): SearchItemTarget | undefined => {
    const navLinkType = option.type !== "page" ? SEARCH_ITEM_NAV_LINK[option.type] : undefined;
    const leagueNavType =
      mode === "usual" && currentLeague && option.type !== "page"
        ? LEAGUE_SEARCH_ITEM_NAV_LINK[option.type]
        : undefined;
    if (leagueNavType && !accessibleLeagueNavTypes.has(leagueNavType)) return undefined;
    if (!leagueNavType && navLinkType && !accessibleNavLinkTypes.has(navLinkType)) return undefined;

    // Admin owns no tournament detail page — the tournament itself lives in
    // Manager, so a tournament row opens Manager in a new tab regardless of the
    // active scope (see docs/global-search.md). Manager deep links need the
    // numeric `internalId`; a hit without one falls through to the tournament's
    // Admin matches page rather than a Manager URL built from the public id,
    // which would silently open the WRONG tournament (see
    // `getTournamentManagerHref`).
    if (option.type === "tournament" && option.internalId != null) {
      return { href: getTournamentManagerHref(option.internalId), isExternal: true };
    }

    const path = getInternalPath(option);

    return path === undefined ? undefined : { href: path, isExternal: false };
  };

  return getSearchItemTarget;
};

/**
 * Opens a resolved {@link SearchItemTarget}: in-place navigation for Admin
 * paths, a new tab for anything that leaves Admin.
 */
export const useOpenSearchItemTarget = () => {
  const navigate = useNavigate();

  return useCallback(
    (target: SearchItemTarget) => {
      if (target.isExternal) {
        window.open(target.href, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(target.href);
    },
    [navigate]
  );
};
